import type { BlockKind, SetType } from './enums.js';
import { isWorkingSetType } from './sets.js';
import type { PrescribedSet } from './types.js';

/**
 * Training-plan domain logic.
 *
 * Lives in `@gym/core` rather than in the repository or the UI because the
 * iPhone will read and eventually edit plans too. Prescription expansion in
 * particular must produce byte-identical rows wherever it runs — the phone
 * materialises a workout from the same prescription the PC authored.
 */

/** Authoring form: one target repeated across N sets. */
export interface UniformPrescription {
  mode: 'uniform';
  sets: number;
  repMin?: number | null;
  repMax?: number | null;
  targetWeightG?: number | null;
  targetDurationS?: number | null;
  /** Metres — see `PrescribedSet.targetDistanceM`. Cardio Phase C1. */
  targetDistanceM?: number | null;
  setType?: SetType;
}

/** Authoring form: each set specified individually, e.g. a pyramid. */
export interface PerSetPrescription {
  mode: 'per_set';
  sets: Array<{
    setType?: SetType;
    repMin?: number | null;
    repMax?: number | null;
    targetWeightG?: number | null;
    targetDurationS?: number | null;
    /** Metres — see `PrescribedSet.targetDistanceM`. Cardio Phase C1. */
    targetDistanceM?: number | null;
  }>;
}

export type PrescriptionSpec = UniformPrescription | PerSetPrescription;

/** Upper bounds, so a typo cannot create a plan with thousands of rows. */
export const MAX_SETS_PER_EXERCISE = 99;
export const MAX_REPS = 1000;
export const MAX_DURATION_SECONDS = 86_400;
/** 1,000 km — the same "a typo cannot create nonsense" bound the others apply. */
export const MAX_DISTANCE_METRES = 1_000_000;

/**
 * Normalise any prescription into per-set rows.
 *
 * "3 × 8–12" becomes three rows of 8–12; a pyramid becomes three rows with
 * differing targets. Everything downstream — the session engine, the
 * progression engine, the phone — therefore reads exactly one shape, and the
 * authoring style never leaks past this function.
 */
export function expandPrescription(spec: PrescriptionSpec): PrescribedSet[] {
  if (spec.mode === 'per_set') {
    return spec.sets.map((set, index) => ({
      setIndex: index + 1,
      dropIndex: 0,
      setType: set.setType ?? 'normal',
      repMin: set.repMin ?? null,
      repMax: set.repMax ?? null,
      targetWeightG: set.targetWeightG ?? null,
      targetDurationS: set.targetDurationS ?? null,
      targetDistanceM: set.targetDistanceM ?? null,
    }));
  }

  return Array.from({ length: spec.sets }, (_, index) => ({
    setIndex: index + 1,
    dropIndex: 0,
    setType: spec.setType ?? 'normal',
    repMin: spec.repMin ?? null,
    repMax: spec.repMax ?? null,
    targetWeightG: spec.targetWeightG ?? null,
    targetDurationS: spec.targetDurationS ?? null,
    targetDistanceM: spec.targetDistanceM ?? null,
  }));
}

/** How many of a prescription's sets count toward completion. */
export function countPrescribedWorkingSets(sets: readonly PrescribedSet[]): number {
  return sets.filter((set) => set.dropIndex === 0 && isWorkingSetType(set.setType)).length;
}

/**
 * The exact planned rep count, when a prescribed set names a single number
 * rather than a genuine range — `repMin === repMax`, e.g. "10–10" meaning
 * "10". Returns `null` for a genuine range (`repMin !== repMax`) or when
 * either bound is unset (Planned Reps as Editable Default, Phase 2).
 *
 * Deliberately narrow: this must never guess a value out of a real range
 * (`8–12`) — that is the same rule `materialise.ts`'s `seedSet()` already
 * follows for why a session set's `reps` starts `null` instead of copying
 * one end of the plan's range. This only ever returns something when there
 * is nothing left to guess.
 */
export function exactTargetReps(target: { repMin: number | null; repMax: number | null }): number | null {
  if (target.repMin === null || target.repMax === null) return null;
  return target.repMin === target.repMax ? target.repMin : null;
}

// ── Validation ──────────────────────────────────────────────────────────────

export type PlanIssueSeverity = 'error' | 'warning';

export interface PlanIssue {
  severity: PlanIssueSeverity;
  code:
    | 'plan_name_empty'
    | 'plan_has_no_days'
    | 'day_name_empty'
    | 'day_has_no_blocks'
    | 'block_has_no_exercises'
    | 'superset_needs_two_exercises'
    | 'exercise_has_no_sets'
    | 'too_many_sets'
    | 'rep_range_inverted'
    | 'reps_out_of_range'
    | 'duration_out_of_range'
    | 'distance_out_of_range'
    | 'hold_without_duration'
    | 'strength_without_reps'
    | 'cardio_without_target'
    | 'duplicate_weekday'
    | 'duplicate_exercise_in_block';
  message: string;
  /** Where the issue is, so the UI can point at it. */
  path: { dayId?: string; blockId?: string; planExerciseId?: string };
}

/** The shape validation needs. Deliberately not the database row type. */
export interface PlanDraft {
  name: string;
  days: Array<{
    id: string;
    name: string;
    weekdays: number[];
    blocks: Array<{
      id: string;
      kind: BlockKind;
      exercises: Array<{
        id: string;
        exerciseId: string;
        /** Modality of the referenced exercise, when known. */
        modality?: string;
        sets: PrescribedSet[];
      }>;
    }>;
  }>;
}

/**
 * Validate a plan.
 *
 * Errors block saving; warnings do not. The distinction matters — a
 * half-finished plan is a normal state to leave the builder in, and refusing to
 * save one would lose work.
 */
export function validatePlan(plan: PlanDraft): PlanIssue[] {
  const issues: PlanIssue[] = [];

  if (plan.name.trim().length === 0) {
    issues.push({
      severity: 'error',
      code: 'plan_name_empty',
      message: 'The plan needs a name.',
      path: {},
    });
  }

  if (plan.days.length === 0) {
    issues.push({
      severity: 'warning',
      code: 'plan_has_no_days',
      message: 'This plan has no training days yet.',
      path: {},
    });
  }

  for (const day of plan.days) {
    if (day.name.trim().length === 0) {
      issues.push({
        severity: 'error',
        code: 'day_name_empty',
        message: 'Every day needs a name.',
        path: { dayId: day.id },
      });
    }

    if (new Set(day.weekdays).size !== day.weekdays.length) {
      issues.push({
        severity: 'error',
        code: 'duplicate_weekday',
        message: `"${day.name}" is scheduled on the same weekday twice.`,
        path: { dayId: day.id },
      });
    }

    if (day.blocks.length === 0) {
      issues.push({
        severity: 'warning',
        code: 'day_has_no_blocks',
        message: `"${day.name}" has no exercises yet.`,
        path: { dayId: day.id },
      });
    }

    for (const block of day.blocks) {
      if (block.exercises.length === 0) {
        issues.push({
          severity: 'warning',
          code: 'block_has_no_exercises',
          message: 'A block has no exercises.',
          path: { dayId: day.id, blockId: block.id },
        });
      }

      // A superset of one is just an exercise, and the workout screen's
      // A1 → B1 → rest ordering would have nothing to alternate with.
      if (block.kind === 'superset' && block.exercises.length < 2) {
        issues.push({
          severity: 'warning',
          code: 'superset_needs_two_exercises',
          message: 'A superset needs at least two exercises to alternate between.',
          path: { dayId: day.id, blockId: block.id },
        });
      }

      const seen = new Set<string>();
      for (const exercise of block.exercises) {
        if (seen.has(exercise.exerciseId)) {
          issues.push({
            severity: 'warning',
            code: 'duplicate_exercise_in_block',
            message: 'The same exercise appears twice in one block.',
            path: { dayId: day.id, blockId: block.id, planExerciseId: exercise.id },
          });
        }
        seen.add(exercise.exerciseId);

        issues.push(
          ...validateExerciseSets(exercise.sets, exercise.modality, {
            dayId: day.id,
            blockId: block.id,
            planExerciseId: exercise.id,
          }),
        );
      }
    }
  }

  return issues;
}

function validateExerciseSets(
  sets: readonly PrescribedSet[],
  modality: string | undefined,
  path: PlanIssue['path'],
): PlanIssue[] {
  const issues: PlanIssue[] = [];

  if (sets.length === 0) {
    issues.push({
      severity: 'warning',
      code: 'exercise_has_no_sets',
      message: 'This exercise has no sets prescribed.',
      path,
    });
    return issues;
  }

  if (sets.length > MAX_SETS_PER_EXERCISE) {
    issues.push({
      severity: 'error',
      code: 'too_many_sets',
      message: `An exercise cannot have more than ${String(MAX_SETS_PER_EXERCISE)} sets.`,
      path,
    });
  }

  for (const set of sets) {
    if (set.repMin !== null && set.repMax !== null && set.repMax < set.repMin) {
      issues.push({
        severity: 'error',
        code: 'rep_range_inverted',
        message: `Set ${String(set.setIndex)}: the maximum reps are below the minimum.`,
        path,
      });
    }

    for (const value of [set.repMin, set.repMax]) {
      if (value !== null && (value < 0 || value > MAX_REPS)) {
        issues.push({
          severity: 'error',
          code: 'reps_out_of_range',
          message: `Set ${String(set.setIndex)}: rep count is out of range.`,
          path,
        });
      }
    }

    if (
      set.targetDurationS !== null &&
      (set.targetDurationS < 0 || set.targetDurationS > MAX_DURATION_SECONDS)
    ) {
      issues.push({
        severity: 'error',
        code: 'duration_out_of_range',
        message: `Set ${String(set.setIndex)}: duration is out of range.`,
        path,
      });
    }

    // Mirrors the duration bound directly above. Distance would otherwise be
    // the only prescribed numeric field with no range check at all, which
    // would let a negative distance reach the database and then produce a
    // negative pace downstream.
    if (
      set.targetDistanceM !== null &&
      (set.targetDistanceM < 0 || set.targetDistanceM > MAX_DISTANCE_METRES)
    ) {
      issues.push({
        severity: 'error',
        code: 'distance_out_of_range',
        message: `Set ${String(set.setIndex)}: distance is out of range.`,
        path,
      });
    }
  }

  // Modality-specific guidance. Warnings only: a plan is often part-finished,
  // and the value can always be decided in the gym.
  if (modality === 'hold' && sets.every((set) => set.targetDurationS === null)) {
    issues.push({
      severity: 'warning',
      code: 'hold_without_duration',
      message: 'This is a timed hold but no duration is prescribed.',
      path,
    });
  }

  if (
    modality === 'strength' &&
    sets.every((set) => set.repMin === null && set.repMax === null)
  ) {
    issues.push({
      severity: 'warning',
      code: 'strength_without_reps',
      message: 'No rep target is prescribed for this exercise.',
      path,
    });
  }

  // Cardio Phase C1. Either target is a complete prescription on its own —
  // "run 5 km" and "run for 20 minutes" are both perfectly well specified —
  // so this warns only when NEITHER is given. Deliberately not an error, and
  // deliberately not a demand for both, matching the warnings-only rule the
  // hold/strength cases above already follow.
  if (
    modality === 'cardio' &&
    sets.every((set) => set.targetDurationS === null && set.targetDistanceM === null)
  ) {
    issues.push({
      severity: 'warning',
      code: 'cardio_without_target',
      message: 'This is a cardio exercise but no duration or distance is prescribed.',
      path,
    });
  }

  return issues;
}

export function hasBlockingIssues(issues: readonly PlanIssue[]): boolean {
  return issues.some((issue) => issue.severity === 'error');
}

// ── Preview ─────────────────────────────────────────────────────────────────

export interface PlanDaySummary {
  exerciseCount: number;
  blockCount: number;
  supersetCount: number;
  /** Prescribed working sets, excluding warm-ups and drop stages. */
  workingSets: number;
  totalSets: number;
  /**
   * Rough time in seconds: working time plus prescribed rest.
   *
   * An estimate, and labelled as one in the UI — actual sessions vary far too
   * much for a precise figure to be honest.
   */
  estimatedSeconds: number;
}

export interface SummariseDayOptions {
  /** Used when neither the block nor the exercise specifies one. */
  defaultRestSeconds: number;
  /** Assumed working time per set. */
  secondsPerSet?: number;
}

export function summarisePlanDay(
  day: PlanDraft['days'][number],
  restByBlock: Readonly<Record<string, number | null>>,
  options: SummariseDayOptions,
): PlanDaySummary {
  const { defaultRestSeconds, secondsPerSet = 45 } = options;

  let exerciseCount = 0;
  let workingSets = 0;
  let totalSets = 0;
  let supersetCount = 0;
  let estimatedSeconds = 0;

  for (const block of day.blocks) {
    if (block.kind === 'superset') supersetCount++;
    const rest = restByBlock[block.id] ?? defaultRestSeconds;

    for (const exercise of block.exercises) {
      exerciseCount++;
      totalSets += exercise.sets.length;

      const working = countPrescribedWorkingSets(exercise.sets);
      workingSets += working;

      // A hold's own duration is the working time; otherwise use the estimate.
      for (const set of exercise.sets) {
        estimatedSeconds += set.targetDurationS ?? secondsPerSet;
      }
    }

    // In a superset the rest falls after the pair, not after each exercise,
    // so a block rests once per round rather than once per exercise.
    const rounds = Math.max(0, ...block.exercises.map((e) => e.sets.length), 0);
    estimatedSeconds += rounds * rest;
  }

  return { exerciseCount, blockCount: day.blocks.length, supersetCount, workingSets, totalSets, estimatedSeconds };
}

// ── Ordering ────────────────────────────────────────────────────────────────

/**
 * Resolve a reorder request into a definitive sequence.
 *
 * The client sends the complete ordered list of ids rather than a "move X to
 * position N" instruction. That makes the operation idempotent and immune to
 * the client and server disagreeing about the current order — and it is why
 * ordering never depends on DOM order.
 *
 * Ids that are missing from the request keep their relative order and follow;
 * unknown ids are ignored. A stale client therefore cannot delete or duplicate
 * anything by reordering.
 */
export function resolveOrder(currentIds: readonly string[], requestedIds: readonly string[]): string[] {
  const known = new Set(currentIds);
  const seen = new Set<string>();
  const ordered: string[] = [];

  for (const id of requestedIds) {
    if (known.has(id) && !seen.has(id)) {
      ordered.push(id);
      seen.add(id);
    }
  }

  for (const id of currentIds) {
    if (!seen.has(id)) ordered.push(id);
  }

  return ordered;
}

/**
 * Resolve a move into another list.
 *
 * Moving an exercise between blocks is the same problem as reordering, with one
 * extra step: the moved id joins the destination's set of known ids before the
 * order is resolved. Everything else — omitted ids following, unknown ids
 * ignored, duplicates collapsed — is `resolveOrder`, unchanged. There is
 * deliberately no separate positioning rule for moves.
 *
 * The moved id is appended before resolving, so a request that forgets to
 * mention it still lands it at the end of the destination rather than losing
 * it.
 */
export function resolveMove(
  destinationIds: readonly string[],
  movedId: string,
  requestedIds: readonly string[],
): string[] {
  const withMoved = [...destinationIds.filter((id) => id !== movedId), movedId];
  return resolveOrder(withMoved, requestedIds);
}

/** Move one item by a relative offset. Backs the up/down controls. */
export function moveInOrder(ids: readonly string[], id: string, delta: number): string[] {
  const index = ids.indexOf(id);
  if (index === -1) return [...ids];

  const target = Math.min(ids.length - 1, Math.max(0, index + delta));
  if (target === index) return [...ids];

  const next = [...ids];
  const [moved] = next.splice(index, 1);
  if (moved !== undefined) next.splice(target, 0, moved);
  return next;
}
